import Connect from "@/assets/js/connect.js"

const getDefaultDateRange = () => {
    const dateTo = new Date()
    const dateFrom = new Date(dateTo.getTime() - (7 * 24 * 60 * 60 * 1000))
    return {
        date_from: dateFrom.toISOString(),
        date_to: dateTo.toISOString(),
    }
}

const buildChartData = (values) => {
    const labels = []
    const points = []

    for (const item of values || []) {
        labels.push(item.created_at)
        points.push(Number(item.value))
    }

    return {
        labels,
        datasets: [
            {
                label: 'Курс',
                data: points,
                borderColor: '#2563eb',
                backgroundColor: 'rgba(37, 99, 235, 0.16)',
                tension: 0.25,
                fill: true,
            }
        ]
    }
}

const defaultRange = getDefaultDateRange()

const state = {
    list: [],
    total: 0,
    loadingList: false,
    selectedRate: null,
    selectedRateId: null,
    values: [],
    valuesTotal: 0,
    chartData: buildChartData([]),
    loadingValues: false,
    loadingChart: false,
    date_from: defaultRange.date_from,
    date_to: defaultRange.date_to,
    listParams: {
        limit: 10,
        skip: 0,
        filter: {},
        sort: {},
    },
    valuesParams: {
        limit: 20,
        skip: 0,
        filter: {},
        sort: {},
    },
}

const getters = {
    StateAdminRates: (state) => state.list,
    StateAdminRatesTotal: (state) => state.total,
    StateAdminRatesLoadingList: (state) => state.loadingList,
    StateAdminSelectedRate: (state) => state.selectedRate,
    StateAdminSelectedRateId: (state) => state.selectedRateId,
    StateAdminRateValues: (state) => state.values,
    StateAdminRateValuesTotal: (state) => state.valuesTotal,
    StateAdminRateChartData: (state) => state.chartData,
    StateAdminRatesLoadingValues: (state) => state.loadingValues,
    StateAdminRatesLoadingChart: (state) => state.loadingChart,
    StateAdminRatesDateFrom: (state) => state.date_from,
    StateAdminRatesDateTo: (state) => state.date_to,
    StateAdminRatesListParams: (state) => state.listParams,
    StateAdminRateValuesParams: (state) => state.valuesParams,
}

const actions = {
    async ActionAdminRatesList({ commit }, payload = {}) {
        const connect = new Connect()
        const limit = payload.limit ?? 10
        const skip = payload.skip ?? 0
        const filter = payload.filter ?? {}
        const sort = payload.sort ?? {}

        commit('setLoadingList', true)
        commit('setListParams', { limit, skip, filter, sort })

        try {
            const response = await connect.GetAdminRatesList(limit, skip, filter, sort)
            if (response && response.result) {
                const data = response.data || {}
                const list = Array.isArray(data.list) ? data.list : Array.isArray(data) ? data : []
                const total = typeof data.total === 'number' ? data.total : list.length
                commit('setRatesList', { list, total })
                return { result: true, data: { list, total } }
            }
            commit('setRatesList', { list: [], total: 0 })
            return { result: false, error: response?.error || null, message: response?.message || null }
        } catch {
            commit('setRatesList', { list: [], total: 0 })
            return { result: false }
        } finally {
            commit('setLoadingList', false)
        }
    },

    async ActionAdminRateValuesList({ commit, state }, payload = {}) {
        const connect = new Connect()
        const limit = payload.limit ?? state.valuesParams.limit
        const skip = payload.skip ?? state.valuesParams.skip
        const sort = payload.sort ?? state.valuesParams.sort
        const date_from = payload.date_from ?? state.date_from
        const date_to = payload.date_to ?? state.date_to
        const selectedRateId = payload.rate_id ?? state.selectedRateId
        const filter = {
            ...(payload.filter ?? state.valuesParams.filter ?? {}),
        }

        if (selectedRateId) {
            filter.rate_id = { value: selectedRateId, matchMode: 'equals' }
        }

        commit('setLoadingValues', true)
        commit('setLoadingChart', true)
        commit('setValuesParams', { limit, skip, filter, sort })
        commit('setDateRange', { date_from, date_to })

        try {
            const response = await connect.GetAdminRatesValuesList(limit, skip, filter, sort, date_from, date_to)
            if (response && response.result) {
                const data = response.data || {}
                const list = Array.isArray(data.list) ? data.list : Array.isArray(data) ? data : []
                const total = typeof data.total === 'number' ? data.total : list.length
                commit('setRateValues', { list, total })
                return { result: true, data: { list, total } }
            }
            commit('setRateValues', { list: [], total: 0 })
            return { result: false, error: response?.error || null, message: response?.message || null }
        } catch {
            commit('setRateValues', { list: [], total: 0 })
            return { result: false }
        } finally {
            commit('setLoadingValues', false)
            commit('setLoadingChart', false)
        }
    },

    ActionAdminSelectRate({ commit }, rate) {
        commit('setSelectedRate', rate || null)
        return { result: true, data: rate || null }
    },

    async ActionAdminLoadRateById({ dispatch, commit }, rateId) {
        if (!rateId) {
            commit('setSelectedRate', null)
            return { result: false }
        }

        const response = await dispatch('ActionAdminRatesList', {
            limit: 1,
            skip: 0,
            filter: {
                _id: { value: rateId, matchMode: 'equals' }
            },
            sort: {}
        })

        if (response.result && response.data && response.data.list && response.data.list[0]) {
            commit('setSelectedRate', response.data.list[0])
            return { result: true, data: response.data.list[0] }
        }

        commit('setSelectedRate', null)
        return { result: false }
    },

    ActionAdminSetRateDateRange({ commit, state }, payload = {}) {
        const date_from = payload.date_from ?? state.date_from
        const date_to = payload.date_to ?? state.date_to
        commit('setDateRange', { date_from, date_to })
        return { result: true }
    },
}

const mutations = {
    setLoadingList(state, value) {
        state.loadingList = value
    },
    setLoadingValues(state, value) {
        state.loadingValues = value
    },
    setLoadingChart(state, value) {
        state.loadingChart = value
    },
    setRatesList(state, payload) {
        state.list = payload.list
        state.total = payload.total
    },
    setSelectedRate(state, rate) {
        state.selectedRate = rate
        state.selectedRateId = rate && (rate._id || rate.id) ? (rate._id || rate.id) : null
    },
    setRateValues(state, payload) {
        state.values = payload.list
        state.valuesTotal = payload.total
        state.chartData = buildChartData(payload.list)
    },
    setDateRange(state, payload) {
        state.date_from = payload.date_from
        state.date_to = payload.date_to
    },
    setListParams(state, payload) {
        state.listParams = payload
    },
    setValuesParams(state, payload) {
        state.valuesParams = payload
    },
}

export default {
    state,
    getters,
    actions,
    mutations,
}